import { Tag, Typography } from 'antd';
import {
  AlertOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  MailOutlined,
  PhoneOutlined,
  SwapOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  ConversationCustomerHelped,
  ConversationOverviewChannel,
  ConversationOverviewFlag
} from '@/api/chat/interfaces';

const { Text } = Typography;

export function ChannelTag({ channel }: { channel: ConversationOverviewChannel }) {
  const { t } = useTranslation('admin');
  if (channel === 'EMAIL') return (
    <Tag icon={<MailOutlined />} color="purple">
      {t('conversationOverview.channel.email')}
    </Tag>
  );
  return channel === 'VOICE' ? (
    <Tag icon={<PhoneOutlined />} color="cyan">
      {t('conversationOverview.channel.call')}
    </Tag>
  ) : (
    <Tag icon={<MessageOutlined />} color="blue">
      {t('conversationOverview.channel.chat')}
    </Tag>
  );
}

export function FlagTag({ flag }: { flag: ConversationOverviewFlag }) {
  const { t } = useTranslation('admin');
  return (
    <Tag
      icon={flag === 'RED' ? <AlertOutlined /> : <CheckCircleOutlined />}
      color={flag === 'RED' ? 'error' : 'success'}
    >
      {flag === 'RED' ? t('conversationOverview.flag.red') : t('conversationOverview.flag.green')}
    </Tag>
  );
}

export function HelpedTag({ value }: { value: ConversationCustomerHelped }) {
  const { t } = useTranslation('admin');
  if (value === 'YES') {
    return (
      <Tag icon={<CheckCircleOutlined />} color="success">
        {t('conversationOverview.yes')}
      </Tag>
    );
  }
  if (value === 'TRANSFERRED') {
    return (
      <Tag icon={<SwapOutlined />} color="processing">
        {t('conversationOverview.transferred')}
      </Tag>
    );
  }
  return (
    <Tag icon={<CloseCircleOutlined />} color="error">
      {t('conversationOverview.no')}
    </Tag>
  );
}

/** Yes/No for boolean flags where "yes" is typically a problem signal. */
export function YesNoTag({ value }: { value: boolean }) {
  const { t } = useTranslation('admin');
  return value ? (
    <Tag icon={<CheckCircleOutlined />} color="error">
      {t('conversationOverview.yes')}
    </Tag>
  ) : (
    <Tag icon={<CloseCircleOutlined />} color="default">
      {t('conversationOverview.no')}
    </Tag>
  );
}

export function HandoffTag({ value }: { value: boolean }) {
  const { t } = useTranslation('admin');
  return value ? (
    <Tag icon={<TeamOutlined />} color="warning">
      {t('conversationOverview.yes')}
    </Tag>
  ) : (
    <Tag icon={<CloseCircleOutlined />} color="default">
      {t('conversationOverview.no')}
    </Tag>
  );
}

/** Compact yes/no used in detail descriptions (no tag when false). */
export function YesNoText({ value }: { value: boolean }) {
  const { t } = useTranslation('admin');
  return value ? (
    <Tag icon={<CheckCircleOutlined />} color="error">
      {t('conversationOverview.yes')}
    </Tag>
  ) : (
    <Text type="secondary">{t('conversationOverview.no')}</Text>
  );
}
